/**
 * TourProvider - Feature Tour Context and State Management
 *
 * Provides tour functionality to the entire application:
 * - Loads tour steps from backend
 * - Pre-flight DOM check (skip steps with missing targets)
 * - Skips conditional steps (e.g. nav-pipeline if pipeline disabled)
 * - Plays per-step Voxtral TTS audio (offline MP3, toggleable)
 * - Manages current step state, navigation
 * - Tracks completion status
 *
 * Usage:
 *   <TourProvider>
 *     <App />
 *   </TourProvider>
 *
 *   const { startTour, currentStep, voiceEnabled, toggleVoice } = useTour();
 */

import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { tourAPI } from '../../../api/index.js';
import { usePipelineOptional } from '../../context/PipelineContext.jsx';
import TourOverlay from './TourOverlay.jsx';
import TourTooltip from './TourTooltip.jsx';

export const TourContext = createContext(null);

const VOICE_LS_KEY = 'fm_tour_voice_enabled';
const AUDIO_BASE = '/audio/tour/';

function readVoicePref() {
  try {
    const v = localStorage.getItem(VOICE_LS_KEY);
    return v === null ? true : v === '1';
  } catch {
    return true;
  }
}

function writeVoicePref(enabled) {
  try {
    localStorage.setItem(VOICE_LS_KEY, enabled ? '1' : '0');
  } catch { /* localStorage may be unavailable */ }
}

function TourProvider({ children }) {
  const pipeline = usePipelineOptional();
  const pipelineEnabled = pipeline?.pipelineEnabled ?? false;

  const [isActive, setIsActive] = useState(false);
  const [tourId, setTourId] = useState('main');
  const [steps, setSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [targetElement, setTargetElement] = useState(null);
  const [voiceEnabled, setVoiceEnabled] = useState(readVoicePref);

  const navigateRef = useRef(null);
  const audioRef = useRef(null);

  const currentStep = steps[currentStepIndex] || null;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;
  const progress = steps.length > 0 ? ((currentStepIndex + 1) / steps.length) * 100 : 0;

  // ───── DOM helpers
  const findTargetForStep = useCallback((step) => {
    const selector = step?.target_selector;
    if (!selector) return null;
    if (selector.startsWith('[')) {
      try { return document.querySelector(selector); } catch { return null; }
    }
    let el = document.querySelector(`[data-tour="${selector}"]`);
    if (!el) {
      try { el = document.querySelector(selector); } catch { return null; }
    }
    return el;
  }, []);

  // ───── Pre-flight: filter steps whose target is missing or conditionally hidden
  const filterReachableSteps = useCallback((rawSteps) => {
    return rawSteps.filter((step) => {
      const sel = step.target_selector || '';

      // Skip pipeline-related steps when pipeline is disabled at tenant level
      if (!pipelineEnabled && /nav-pipeline|"pipeline"\]/.test(sel)) {
        console.info('[Tour] Skipping pipeline step (pipeline disabled):', step.title);
        return false;
      }

      // Skip steps whose target doesn't exist at all in the DOM (only check static
      // anchors that should be present at tour start, i.e. layout/nav). For
      // dynamically-rendered targets the target retry loop handles it later.
      if (sel.includes('nav-') || sel.includes('sidebar') ||
          sel.includes('user-menu') || sel.includes('help-button') ||
          sel.includes('global-search')) {
        const found = findTargetForStep(step);
        if (!found) {
          console.warn('[Tour] Pre-flight: target missing, skipping:', sel);
          return false;
        }
      }
      return true;
    });
  }, [pipelineEnabled, findTargetForStep]);

  // ───── Audio playback
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch { /* ignore */ }
      audioRef.current = null;
    }
  }, []);

  const playStepAudio = useCallback((step) => {
    stopAudio();
    if (!step?.audio_filename || !voiceEnabled) return;
    try {
      const audio = new Audio(`${AUDIO_BASE}${step.audio_filename}`);
      audio.preload = 'auto';
      audioRef.current = audio;
      audio.play().catch((err) => {
        // Autoplay may be blocked until first user gesture; silently degrade.
        console.debug('[Tour] Audio play blocked:', err?.message);
      });
    } catch (err) {
      console.warn('[Tour] Audio init failed:', err?.message);
    }
  }, [voiceEnabled, stopAudio]);

  const toggleVoice = useCallback(() => {
    setVoiceEnabled((prev) => {
      const next = !prev;
      writeVoicePref(next);
      if (!next) {
        stopAudio();
      } else if (currentStep) {
        playStepAudio(currentStep);
      }
      return next;
    });
  }, [currentStep, playStepAudio, stopAudio]);

  // ───── Find/highlight target for current step
  useEffect(() => {
    if (!isActive || !currentStep) {
      setTargetElement(null);
      return;
    }

    const selector = currentStep.target_selector || '';
    const isNavStep = selector.includes('nav-') || selector.includes('sidebar-item');
    const isDashboardStep = selector.includes('dashboard') ||
                            selector.includes('global-search') ||
                            selector.includes('user-menu') ||
                            selector.includes('help-button') ||
                            selector.includes('sidebar"]');

    if (isDashboardStep && navigateRef.current) {
      navigateRef.current('dashboard');
      const t = setTimeout(() => setTargetElement(findTargetForStep(currentStep)), 200);
      return () => clearTimeout(t);
    }

    if (isNavStep) {
      const navEl = findTargetForStep(currentStep);
      if (navEl) {
        navEl.click();
        const t = setTimeout(() => setTargetElement(navEl), 300);
        return () => clearTimeout(t);
      }
    }

    const el = findTargetForStep(currentStep);
    setTargetElement(el);

    if (!el) {
      let attempts = 0;
      const maxAttempts = 5;
      const interval = setInterval(() => {
        attempts++;
        const found = findTargetForStep(currentStep);
        if (found || attempts >= maxAttempts) {
          setTargetElement(found);
          clearInterval(interval);
        }
      }, 300);
      return () => clearInterval(interval);
    }
  }, [isActive, currentStep, findTargetForStep]);

  // ───── Scroll target into view
  useEffect(() => {
    if (targetElement && isActive) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  }, [targetElement, isActive]);

  // ───── Play audio when step changes
  useEffect(() => {
    if (isActive && currentStep) {
      playStepAudio(currentStep);
    }
    return () => {
      // Stop on unmount of effect (step change or tour end)
      // Note: we DON'T stop here because we want the step to keep playing.
      // The next playStepAudio() call will stop the previous one.
    };
  }, [isActive, currentStep, playStepAudio]);

  // ───── Stop audio when tour ends
  useEffect(() => {
    if (!isActive) stopAudio();
  }, [isActive, stopAudio]);

  // ───── Register navigate function
  const registerNavigate = useCallback((navigateFn) => {
    navigateRef.current = navigateFn;
  }, []);

  // ───── Start tour
  const startTour = useCallback(async (id = 'main') => {
    setLoading(true);
    try {
      const response = await tourAPI.getSteps(id);
      const rawSteps = response.ok && response.data.steps?.length > 0
        ? response.data.steps
        : [];

      if (rawSteps.length === 0) {
        console.warn('[Tour] No steps for tour:', id);
        return;
      }

      // Navigate to the first step's declared view before pre-flight,
      // so that view-specific anchors (e.g. pipeline-page) exist in the DOM
      // when filterReachableSteps inspects them.
      const firstView = rawSteps[0]?.view_code || 'dashboard';
      if (navigateRef.current) {
        navigateRef.current(firstView);
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      const reachable = filterReachableSteps(rawSteps);

      if (reachable.length === 0) {
        console.warn('[Tour] All steps filtered out (no reachable target). Tour aborted.');
        return;
      }

      setTourId(id);
      setSteps(reachable);
      setCurrentStepIndex(0);
      setIsActive(true);
    } catch (err) {
      console.error('[Tour] Failed to load steps:', err);
    } finally {
      setLoading(false);
    }
  }, [filterReachableSteps]);

  // ───── Step navigation
  const nextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(i => i + 1);
    }
  }, [currentStepIndex, steps.length]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(i => i - 1);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback((index) => {
    if (index >= 0 && index < steps.length) {
      setCurrentStepIndex(index);
    }
  }, [steps.length]);

  // ───── End tour
  const endTour = useCallback(async (completed = false) => {
    stopAudio();
    if (completed) {
      try { await tourAPI.complete(tourId); }
      catch (err) { console.error('[Tour] Failed to mark complete:', err); }
    }
    if (navigateRef.current) navigateRef.current('dashboard');
    setIsActive(false);
    setSteps([]);
    setCurrentStepIndex(0);
    setTargetElement(null);
  }, [tourId, stopAudio]);

  const completeTour = useCallback(() => endTour(true), [endTour]);
  const skipTour = useCallback(() => endTour(false), [endTour]);

  // ───── Keyboard
  useEffect(() => {
    if (!isActive) return;
    const handler = (e) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'Enter':
          if (!isLastStep) nextStep(); else completeTour();
          break;
        case 'ArrowLeft':
          prevStep();
          break;
        case 'Escape':
          skipTour();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isActive, isLastStep, nextStep, prevStep, completeTour, skipTour]);

  const contextValue = {
    isActive,
    loading,
    tourId,
    steps,
    currentStep,
    currentStepIndex,
    targetElement,
    isFirstStep,
    isLastStep,
    progress,
    voiceEnabled,
    startTour,
    nextStep,
    prevStep,
    goToStep,
    completeTour,
    skipTour,
    toggleVoice,
    registerNavigate,
  };

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      {isActive && currentStep && (
        <>
          <TourOverlay targetElement={targetElement} />
          <TourTooltip
            step={currentStep}
            targetElement={targetElement}
            stepIndex={currentStepIndex}
            totalSteps={steps.length}
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            voiceEnabled={voiceEnabled}
            onNext={nextStep}
            onPrev={prevStep}
            onSkip={skipTour}
            onComplete={completeTour}
            onToggleVoice={toggleVoice}
          />
        </>
      )}
    </TourContext.Provider>
  );
}

export default TourProvider;
